/**
 * BannerInquiryUnreadContext
 * - 사용자(문의자)의 1:1 비공개 문의(BANNER_INQUIRY) 미읽음을 앱 전역에서 실시간 계산.
 * - Firestore onSnapshot 만 사용 (폴링 없음). 종 뱃지 + 베너 상세 1:1 버튼 뱃지에서 공유.
 * - 미읽음 = 광고주/운영자가 보낸 메시지 중, 본인 읽음커서(last_read) 이후 것.
 */
import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { useAuthVersion } from "../hooks/useAuthVersion";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db, ensureFirestoreAuth } from "../firebase";
import { EnumBoardType } from "../constants/enums";

interface InquiryUnreadCtx {
  total: number;
  perBanner: Record<number, number>;
}

const Ctx = createContext<InquiryUnreadCtx>({ total: 0, perBanner: {} });

export function useBannerInquiryUnread() {
  return useContext(Ctx);
}

export function BannerInquiryUnreadProvider({ children }: { children: ReactNode }) {
  const [perBanner, setPerBanner] = useState<Record<number, number>>({});
  useAuthVersion(); // 로그인/로그아웃 시 재렌더 → 아래 myUserId 재계산 → effect 재구독 (reload 대체)
  const myUserId = parseInt(localStorage.getItem("user_id") || "0");
  const isLoggedIn = !!localStorage.getItem("access_token");

  useEffect(() => {
    if (!myUserId || !isLoggedIn) {
      setPerBanner({});
      return;
    }
    let unsubMsg: (() => void) | null = null;
    let unsubCur: (() => void) | null = null;
    let canceled = false;

    // 메시지 / 커서 raw 보관 → 둘 중 하나 갱신 시 재계산
    let msgs: Array<{ banner_id: number; comment_id: number; user_id: number }> = [];
    let cursors: Record<number, number> = {};

    const recompute = () => {
      const per: Record<number, number> = {};
      for (const m of msgs) {
        if (m.user_id === myUserId) continue; // 내가 보낸 건 제외
        const lastRead = cursors[m.banner_id] || 0;
        if (m.comment_id > lastRead) per[m.banner_id] = (per[m.banner_id] || 0) + 1;
      }
      setPerBanner(per);
    };

    (async () => {
      const ok = await ensureFirestoreAuth();
      if (!ok || canceled) return;

      // 본인이 문의자(chat_user_id)인 모든 BANNER_INQUIRY 메시지
      const qMsg = query(
        collection(db, "trippang_comments"),
        where("board_type", "==", EnumBoardType.BANNER_INQUIRY),
        where("chat_user_id", "==", myUserId),
      );
      unsubMsg = onSnapshot(
        qMsg,
        (snap) => {
          msgs = snap.docs.map((d) => {
            const x = d.data() as { target_id?: number; comment_id?: number; user_id?: number };
            return { banner_id: x.target_id ?? 0, comment_id: x.comment_id ?? 0, user_id: x.user_id ?? 0 };
          });
          recompute();
        },
        (err) => console.warn("[InquiryUnread] msg onSnapshot error:", err),
      );

      // 본인 읽음 커서 (사용자측은 chat_user_id 미사용 → target_id 별)
      const qCur = query(
        collection(db, "trippang_read_cursors"),
        where("board_type", "==", EnumBoardType.BANNER_INQUIRY),
        where("user_id", "==", myUserId),
      );
      unsubCur = onSnapshot(
        qCur,
        (snap) => {
          const m: Record<number, number> = {};
          snap.docs.forEach((d) => {
            const x = d.data() as { target_id?: number; last_read_comment_id?: number };
            if (x.target_id != null) {
              m[x.target_id] = Math.max(m[x.target_id] || 0, x.last_read_comment_id || 0);
            }
          });
          cursors = m;
          recompute();
        },
        (err) => console.warn("[InquiryUnread] cursor onSnapshot error:", err),
      );
    })();

    return () => {
      canceled = true;
      if (unsubMsg) unsubMsg();
      if (unsubCur) unsubCur();
    };
  }, [myUserId, isLoggedIn]);

  const total = Object.values(perBanner).reduce((a, b) => a + b, 0);

  return <Ctx.Provider value={{ total, perBanner }}>{children}</Ctx.Provider>;
}
